import { type ComponentProps } from "react";
import { cn } from "@/lib/utils";
import { CHIP_CLASSES } from "../variableTokenConstants";
import { PrintTableColumnChips } from "./printTableColumnChips";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import type { DirectiveChipSegmentButtonFn } from "../../types/directiveChipTypes";

export type DirectiveChipTriggerOwnProps = {
  directiveLabel: string;
  variablePart: string;
  choicesPart: readonly string[];
  strategy: DirectiveChipStrategy | null;
  segmentButton: DirectiveChipSegmentButtonFn;
  onReorderPrintTableChoices: (fromIndex: number, toIndex: number) => void;
  onToggleOpenFromKeyboard: () => void;
};

export type DirectiveChipTriggerProps = DirectiveChipTriggerOwnProps &
  Omit<ComponentProps<"span">, "children">;

/**
 * Inline chip: @directive / variable : choices (optional print(table) sortable columns).
 * Receives `ref` as a normal prop (React 19) for Radix PopoverTrigger (asChild).
 */
export const DirectiveChipTrigger = ({
  ref,
  directiveLabel,
  variablePart,
  choicesPart,
  strategy,
  segmentButton,
  onReorderPrintTableChoices,
  onToggleOpenFromKeyboard,
  className,
  onKeyDown,
  ...rest
}: DirectiveChipTriggerProps) => {
  return (
    <span
      ref={ref}
      {...rest}
      className={cn(CHIP_CLASSES, className)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleOpenFromKeyboard();
        }
      }}
    >
      {segmentButton("directive", `@${directiveLabel}`)}
      <span className="opacity-70">/</span>
      {variablePart ? (
        <>
          {segmentButton("variable", variablePart)}
          {strategy != null && choicesPart.length > 1 ? (
            <PrintTableColumnChips
              choices={choicesPart}
              onReorder={onReorderPrintTableChoices}
              segmentButton={segmentButton}
            />
          ) : (
            choicesPart.map((c, choiceIndex) => (
              <span key={`${c}::${String(choiceIndex)}`}>
                <span className="opacity-70">:</span>
                {segmentButton(choiceIndex, c)}
              </span>
            ))
          )}
        </>
      ) : (
        segmentButton("variable", "variable", true)
      )}
    </span>
  );
};
